import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { scrollToId } from "../lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "16%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative flex h-[100svh] min-h-[620px] items-end overflow-hidden bg-espresso"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <motion.img
          src="/media/hero.jpg"
          alt={t.hero.imageAlt}
          className="h-full w-full object-cover"
          initial={reduce ? { opacity: 1 } : { opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.2, ease: EASE }}
        />
        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-gradient-to-t from-espresso/85 via-espresso/30 to-espresso/35"
        />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 sm:px-8 sm:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          className="text-xs uppercase tracking-[0.3em] text-cream/75"
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="mt-6 font-serif font-light leading-[1.04] tracking-tight text-cream">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block text-[clamp(2.4rem,9.5vw,5.9rem)]"
                initial={reduce ? { opacity: 0 } : { y: "112%" }}
                animate={reduce ? { opacity: 1 } : { y: 0 }}
                transition={{ duration: 1.15, delay: 0.3 + i * 0.18, ease: EASE }}
                data-testid={`hero-line-${i + 1}`}
              >
                {i === 1 ? <em className="font-light italic">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.85, ease: EASE }}
          className="mt-6 max-w-md text-base leading-relaxed text-cream/85 sm:text-lg"
          data-testid="hero-sub"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-6"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollToId("commission")}
            className="rounded-full bg-cream px-8 py-4 text-[11px] uppercase tracking-[0.2em] text-espresso transition-colors duration-300 hover:bg-ochre"
          >
            {t.hero.ctaPrimary}
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollToId("story")}
            className="link-underline text-[11px] uppercase tracking-[0.2em] text-cream"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("story")}
        aria-label={t.hero.scroll}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 sm:flex"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-cream/60">{t.hero.scroll}</span>
        <motion.span
          className="block h-12 w-px origin-top bg-cream/50"
          animate={reduce ? {} : { scaleY: [0.2, 1, 0.2], opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.button>
    </section>
  );
}
